import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageShell, Breadcrumbs } from "@/components/layout/PageShell";
import { MenuItemImage } from "@/components/menu/MenuItemImage";
import { MenuItemPreviewDialog } from "@/components/menu/MenuItemPreviewDialog";
import { PriceDisplay } from "@/components/menu/PriceDisplay";
import { CategoryNavigation } from "@/components/menu/CategoryNavigation";
import { EmptyState, ErrorState, Spinner } from "@/components/states";
import { categoriesFromMenu, menuItemsFromMenu } from "@/lib/api";
import { branchesQuery, publicBranchMenuQuery } from "@/lib/queries";
import { loc } from "@/lib/format";
import { activeMenuCategory, menuItemDisplayPricing } from "@/lib/menu-model";
import type { MenuItem } from "@/lib/types";
import { useI18n } from "@/lib/i18n";
import { useStorefront } from "@/lib/storefront";

export const Route = createFileRoute("/branches_/$branchId_/menu")({
  component: BranchPublicMenu,
});

function BranchPublicMenu() {
  const { branchId } = Route.useParams();
  const [categoryId, setCategoryId] = useState("");
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const { lang, t } = useI18n();
  const storefront = useStorefront();
  const branches = useQuery(branchesQuery());
  const branch = branches.data?.find((item) => item.id === branchId);
  const onlineEnabled = Boolean(storefront.settings?.onlineOrderingEnabled);
  const indoorEnabled = Boolean(
    storefront.settings?.dineInEnabled && branch?.indoorService,
  );
  const menuAvailable = onlineEnabled || indoorEnabled;
  const menu = useQuery(
    publicBranchMenuQuery(branch ? branchId : "", onlineEnabled, indoorEnabled),
  );

  const categories = menu.data ? categoriesFromMenu(menu.data.data) : [];
  const items = menu.data
    ? menuItemsFromMenu(menu.data.data, {
        branchId,
        orderType: menu.data.orderType,
        limit: menu.data.data.items.length,
      }).data.filter((item) =>
        menu.data.orderType === "indoor"
          ? item.indoorAvailable
          : item.onlineAvailable,
      )
    : [];
  const populatedCategories = categories
    .map((category) => ({
      category,
      items: items.filter((item) => item.categoryId === category.id),
    }))
    .filter(({ items: categoryItems }) => categoryItems.length > 0);
  const activeCategoryId = activeMenuCategory(
    populatedCategories.map(({ category }) => category),
    categoryId,
  );
  const activeCategory = populatedCategories.find(
    ({ category }) => category.id === activeCategoryId,
  );

  if (branches.isPending)
    return (
      <PageShell>
        <Spinner />
      </PageShell>
    );
  if (branches.isError)
    return (
      <PageShell>
        <ErrorState error={branches.error} onRetry={() => branches.refetch()} />
      </PageShell>
    );
  if (!branch)
    return (
      <PageShell>
        <EmptyState
          title={t("notfound.title")}
          description={t("notfound.desc")}
        />
      </PageShell>
    );
  if (!menuAvailable)
    return (
      <PageShell>
        <EmptyState
          title={lang === "ar" ? "القائمة غير متاحة" : "Menu unavailable"}
          description={
            lang === "ar"
              ? "تحقق مرة أخرى لاحقًا."
              : "Please check again later."
          }
        />
      </PageShell>
    );

  const name = loc(branch.name, lang);
  return (
    <PageShell>
      <Breadcrumbs
        items={[
          { label: t("nav.home"), to: "/" },
          { label: t("nav.branches"), to: "/branches" },
          { label: name, to: `/branches/${branchId}` },
          { label: t("nav.menu") },
        ]}
      />
      <h1 className="text-3xl font-black sm:text-4xl">
        {lang === "ar" ? `قائمة ${name}` : `${name} menu`}
      </h1>
      <p className="mt-2 text-muted-foreground">{loc(branch.address, lang)}</p>
      <p className="mt-2 text-sm text-muted-foreground">
        {lang === "ar"
          ? "تعرض هذه الصفحة الأصناف المتاحة في هذا الفرع."
          : "This menu shows items available at this branch."}
      </p>

      {menu.isPending ? (
        <div className="py-12">
          <Spinner />
        </div>
      ) : menu.isError ? (
        <div className="py-8">
          <ErrorState error={menu.error} onRetry={() => menu.refetch()} />
        </div>
      ) : items.length === 0 ? (
        <div className="py-8">
          <EmptyState
            title={lang === "ar" ? "القائمة غير متاحة" : "Menu unavailable"}
            description={
              lang === "ar"
                ? "تحقق مرة أخرى لاحقًا."
                : "Please check again later."
            }
          />
        </div>
      ) : (
        <div className="mt-8 grid min-w-0 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
          <CategoryNavigation
            categories={populatedCategories.map(({ category }) => category)}
            activeCategoryId={activeCategoryId}
            searching={false}
            onSelect={setCategoryId}
          />
          <section className="min-w-0">
            <h2 className="mb-5 text-2xl font-bold">
              {loc(activeCategory?.category.name, lang)}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {activeCategory?.items.map((item) => (
                <article
                  key={item.id}
                  className="surface-card relative flex gap-4 p-4 transition-shadow hover:shadow-lift"
                >
                  <button
                    type="button"
                    className="absolute inset-0 cursor-pointer rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
                    onClick={() => setSelectedItem(item)}
                    aria-label={loc(item.name, lang)}
                  />
                  <MenuItemImage
                    source={item.responsiveImage ?? item.image}
                    name={loc(item.name, lang)}
                    sizes="96px"
                    loading="lazy"
                    className="size-24 shrink-0 rounded-xl object-cover"
                  />
                  <div className="min-w-0">
                    <h3 className="font-bold">{loc(item.name, lang)}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {loc(item.description, lang)}
                    </p>
                    <p className="mt-2 font-bold text-foreground">
                      <PriceDisplay
                        {...(menu.data.orderType === "indoor"
                          ? {
                              price: item.type === "variable" && item.variations.length
                                ? Math.min(...item.variations.map((variation) => variation.branchPrice))
                                : item.branchPrice,
                              originalPrice: null,
                            }
                          : menuItemDisplayPricing(item))}
                      />
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}
      {selectedItem ? (
        <MenuItemPreviewDialog
          key={selectedItem.id}
          item={selectedItem}
          open
          onOpenChange={(open) => { if (!open) setSelectedItem(null); }}
          priceMode={menu.data?.orderType === "indoor" ? "indoor" : "online"}
        />
      ) : null}
      <Link
        to="/branches/$branchId"
        params={{ branchId }}
        className="mt-10 inline-flex min-h-11 items-center text-sm font-bold text-primary underline-offset-4 hover:underline"
      >
        {lang === "ar" ? "تفاصيل الفرع" : "Branch details"}
      </Link>
    </PageShell>
  );
}
